/**
 * Run 12 item B (DECIDED 20261012, Barna): "English is allowed for every
 * customer conversation: the page, every customer email and customer PDFs."
 *
 * The English customer emails, one per Hungarian customer template, on the
 * same blocks and the same layout. They are chosen by `buildEmail` when the
 * order was placed in English; the Hungarian templates in `templates.ts` are
 * unchanged. Staff, finance and kitchen emails stay Hungarian, and
 * szamlazz.hu documents stay Hungarian (`invoice_language`).
 *
 * The copy rules are the Hungarian ones carried over, and the page's English
 * where the page already says the same thing:
 *   * the company is *The Planteen* (run 9 item 1), in the first person plural;
 *   * money as the page writes it in English — *HUF 4,118*; dates *Thu 15 Oct
 *     2026*, the long form *Thursday, 15 October 2026*, a deadline *Thu 15 Oct
 *     2026 09:05* (DR-12, `build/FIXES.md` item 15);
 *   * the transfer sentence, the card sentence and the fee ladder are the
 *     page's own English (Step4Summary), word for word;
 *   * a gap the Hungarian marks with *HIÁNYZÓ SZÖVEG/ADAT* is marked here too,
 *     in English, rather than filled;
 *   * no production unit, margin, capacity, review reason or price per dish.
 *
 * This English is a first draft for the review pack (run 12 item 3e): the
 * reviewer's wording questions are listed there, not settled here.
 */

import { legalLinks, termsAttachedNote } from './legalLinks.ts'
import type { Block, Cell, Email } from './layout.ts'
import { parseDate, time } from './format.ts'
import { allergensAreVerified, CATEGORY_ORDER, dishName, fulfilmentWhen, isLookalike, markedName, mealNoteOf, nameMarks, PLANT_BASED_LOOKALIKE_NOTE_EN, shown, withMealNote } from './osszesites.ts'
import { cardLinkOnDijbekero, FEE_TERM_KEYS, type EmailPayload, type FeeTermKey, type InvoicePayload, type OrderLine, type OrderPayload, type PriceCategory } from './payload.ts'

/* ------------------------------------------------------------------ *
 * Formats — the page's English (apps/web/src/page/format.ts)
 * ------------------------------------------------------------------ */

const NBSP = ' '
const MINUS = '−'
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const

/** *4,118* — comma groups from four digits, decimal point. */
export function nEn(value: number, decimals = 0): string {
  const negative = value < 0
  const [whole, fraction] = Math.abs(Number(value) || 0).toFixed(decimals).split('.')
  const grouped = (whole ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return (negative ? MINUS : '') + grouped + (fraction ? '.' + fraction : '')
}

/** *HUF 4,118*, as the page's English price. */
export const ftEn = (value: number): string => 'HUF' + NBSP + nEn(Math.round(Number(value) || 0))

const dayOfWeek = (y: number, m: number, d: number): number => new Date(Date.UTC(y, m - 1, d)).getUTCDay()

/** *15 Oct 2026*, and with the weekday *Thu 15 Oct 2026*. */
export function dateEn(value: string, withWeekday = false): string {
  const p = parseDate(value)
  if (p === null) return ''
  const short = `${p.d} ${(MONTHS[p.m - 1] ?? '').slice(0, 3)} ${p.y}`
  return withWeekday ? `${(WEEKDAYS[dayOfWeek(p.y, p.m, p.d)] ?? '').slice(0, 3)} ${short}` : short
}

/** B73: *Monday 2 November*, for a subject line in place of *tomorrow*. */
export function onDayEn(value: string): string {
  const p = parseDate(value)
  if (p === null) return ''
  return `${WEEKDAYS[dayOfWeek(p.y, p.m, p.d)]} ${p.d} ${MONTHS[p.m - 1]}`
}

/** *Thursday, 15 October 2026*. */
export function longDateEn(value: string): string {
  const p = parseDate(value)
  if (p === null) return ''
  return `${WEEKDAYS[dayOfWeek(p.y, p.m, p.d)]}, ${p.d} ${MONTHS[p.m - 1]} ${p.y}`
}

/** *Thu 15 Oct 2026 09:05*, as the page prints a deadline. */
export function dateTimeEn(value: string | null | undefined): string {
  const raw = String(value ?? '')
  const d = dateEn(raw, true)
  const t = time(raw.slice(11))
  if (d === '') return ''
  return t === '' ? d : `${d} ${t}`
}

/** *db* reads *pcs*, *adag* reads *portion(s)*, as on the page. */
export function qtyEn(quantity: number, unit?: string | null): string {
  if (!unit) return nEn(quantity)
  const word = unit === 'adag' ? (quantity === 1 ? 'portion' : 'portions') : unit === 'db' ? 'pcs' : unit
  return `${nEn(quantity)}${NBSP}${word}`
}

const pctEn = (value: number): string => `${nEn(value)}%`

/* ------------------------------------------------------------------ *
 * The summary (§5.11), in English
 * ------------------------------------------------------------------ */

const CATEGORY_LABEL_EN: Record<PriceCategory, string> = {
  sos: 'Savoury',
  edes: 'Sweet',
  gyumolcs: 'Fruit',
  ital: 'Drinks',
}

/** The 14 allergens of 1169/2011/EU Annex II — the page's English names
 *  (`apps/web/src/page/catalogue.ts`). The codes are the line's own. */
const ALLERGEN_EN: Record<number, string> = {
  1: 'cereals containing gluten',
  2: 'crustaceans',
  3: 'eggs',
  4: 'fish',
  5: 'peanuts',
  6: 'soybeans',
  7: 'milk',
  8: 'tree nuts',
  9: 'celery',
  10: 'mustard',
  11: 'sesame seeds',
  12: 'sulphur dioxide and sulphites',
  13: 'lupin',
  14: 'molluscs',
}

export const ALLERGENS_BEING_VERIFIED_EN = 'Allergens being checked'

export function allergenTextEn(line: OrderLine): string {
  if (!allergensAreVerified(line)) return ALLERGENS_BEING_VERIFIED_EN
  const parts = (line.allergens ?? []).map((allergen) => {
    const name = ALLERGEN_EN[Number(allergen.code)]
    return name ? `${allergen.code} ${name}` : String(allergen.code)
  })
  return parts.length === 0 ? 'allergens: none' : `allergens: ${parts.join(' · ')}`
}

const right = (text: string, strong = false): Cell => ({ text, align: 'right', strong })

const dishCell = (line: OrderLine): Exclude<Cell, string> => {
  // Run 12 item F: the marked expressions only, each with its asterisk.
  const { text, italics } = markedName(dishName(line, 'en'), nameMarks(line, 'en'))
  const suffix = line.is_gf_portion ? ' — gluten-free, separate box' : ''
  return { text: `${text}${suffix}`, italics, indent: true }
}

export function eventLineEn(order: OrderPayload): string {
  const parts: string[] = []
  parts.push(
    order.gf_guests > 0
      ? `${nEn(order.guests)} guests (${nEn(order.gf_guests)} gluten-free)`
      : `${nEn(order.guests)} guests`,
  )
  parts.push(longDateEn(order.event_date))
  const start = time(order.event_start)
  if (start !== '') parts.push(start)
  const occasion = order.occasion_label_en ?? null
  /* Run 19 §4: the meal note beside the occasion name, here as everywhere. */
  if (occasion) parts.push(withMealNote(occasion, mealNoteOf(order, 'en')))
  return parts.join(' · ')
}

function foodTableEn(order: OrderPayload): Block {
  const rows: Cell[][] = []
  let foodNet = 0
  let foodGross = 0
  for (const category of CATEGORY_ORDER) {
    const total = order.totals_by_category[category]
    const lines = order.lines.filter((line) => line.price_category === category)
    if (total === undefined && lines.length === 0) continue
    const amount = total ?? { quantity: 0, unit: null, net: 0, vat: 0, gross: 0 }
    foodNet += amount.net
    foodGross += amount.gross
    const gf = lines.filter((line) => line.is_gf_portion).reduce((sum, line) => sum + line.quantity, 0)
    rows.push([
      {
        text: gf > 0 ? `${CATEGORY_LABEL_EN[category]} · of which gluten-free: ${qtyEn(gf, amount.unit)}` : CATEGORY_LABEL_EN[category],
        strong: true,
      },
      right(qtyEn(amount.quantity, amount.unit), true),
      right(ftEn(shown(order, amount)), true),
    ])
    for (const line of lines) {
      rows.push([dishCell(line), right(qtyEn(line.quantity, line.unit)), right('')])
      rows.push([{ text: allergenTextEn(line), indent: true, muted: true }, right(''), right('')])
    }
  }
  rows.push([{ text: 'Food and drinks in total', strong: true }, right(''), right(ftEn(order.is_business ? foodNet : foodGross), true)])
  return {
    kind: 'table',
    head: ['Category', right('Quantity'), right(order.is_business ? 'Price (net)' : 'Price (gross)')],
    rows,
  }
}

function serviceTableEn(order: OrderPayload): Block {
  const rows: Cell[][] = []
  if (order.fulfilment === 'pickup') {
    rows.push([{ text: 'Collection' }, right('free')])
  } else {
    const delivery = order.totals_by_category.szallitas ?? {
      quantity: 1,
      unit: null,
      net: Math.round(order.delivery_fee / (1 + order.vat_pct / 100)),
      vat: 0,
      gross: order.delivery_fee,
    }
    const amount = shown(order, delivery)
    const label = order.zone ? `Delivery · zone ${order.zone}` : 'Delivery'
    const value = order.zone === 'C' ? 'quoted individually' : amount === 0 ? 'free' : ftEn(amount)
    rows.push([{ text: label }, right(value)])
  }
  const SERVICE_EN = { felszereles: 'Equipment', szemelyzet: 'Staff' } as const
  for (const key of ['felszereles', 'szemelyzet'] as const) {
    const amount = order.totals_by_category[key]
    if (amount === undefined) continue
    rows.push([{ text: SERVICE_EN[key] }, right(ftEn(shown(order, amount)))])
  }
  return { kind: 'table', head: ['Service', right(order.is_business ? 'Price (net)' : 'Price (gross)')], rows }
}

function totalsBlockEn(order: OrderPayload): Block {
  const guests = Math.max(order.guests, 1)
  const rows: [string, string][] = []
  if (order.is_business) {
    rows.push(['Net total', ftEn(order.net)])
    rows.push([`VAT ${nEn(order.vat_pct)}%`, ftEn(order.vat)])
    rows.push(['Gross total, to pay', ftEn(order.gross)])
    rows.push(['Per guest, net', ftEn(Math.round(order.net / guests))])
  } else {
    rows.push(['Total, gross', ftEn(order.gross)])
    rows.push(['Per guest, gross', ftEn(Math.round(order.gross / guests))])
  }
  return { kind: 'facts', rows }
}

const eventBlockEn = (order: OrderPayload): Block => ({
  kind: 'facts',
  rows: [
    ['Order reference', order.ref],
    ['Date and guests', eventLineEn(order)],
    ...(order.event_name ? ([['Event name', order.event_name]] as [string, string][]) : []),
    [
      'Fulfilment',
      /* Run 16 item 2: the label is the one that was here; the time beside it
         carries its date and its weekday, from the one shared formatter. */
      order.fulfilment === 'pickup'
        ? `Collection${fulfilmentWhen(order, 'en') ? ` · ${fulfilmentWhen(order, 'en')}` : ''}`
        : `Delivery${fulfilmentWhen(order, 'en') ? ` · ${fulfilmentWhen(order, 'en')}` : ''}`,
    ],
  ],
})

/* Run 19 §14: *Our kitchen is not certified gluten-free, so we cannot
   guarantee freedom from cross-contamination for guests with coeliac
   disease.* The first sentence, which is about packing and not about the
   kitchen, is unchanged. */
export const GLUTEN_FREE_NOTICE_EN =
  'We deliver the gluten-free dishes in separate, labelled boxes. Our kitchen is not certified ' +
  'gluten-free, so we cannot guarantee freedom from cross-contamination for guests with coeliac disease.'

export const ALLERGEN_EXAMPLE_NOTICE_EN =
  'Allergen data marked as an example has not been checked yet and is for information only. ' +
  'We send the checked list once the allergen data has been approved.'

/** Run 19 §14, Fede's wording: the line under *Total*. */
export const PLANT_BASED_LINE_EN =
  'Everything we offer is 100% plant-based. Quantities are professional estimates, ' +
  'based on the events we have catered.'

export const lookalikeNoteEn = (lines: readonly OrderLine[]): Block | null =>
  lines.some((line) => isLookalike(line, 'en')) ? { kind: 'note', text: PLANT_BASED_LOOKALIKE_NOTE_EN } : null

export function summaryEn(order: OrderPayload): Block[] {
  /* Run 19 §14: directly under the food and drink total, where the marks
     it explains are. */
  const footnote = lookalikeNoteEn(order.lines)
  const blocks: Block[] = [
    { kind: 'heading', text: 'Your event' },
    eventBlockEn(order),
    { kind: 'heading', text: 'Food' },
    foodTableEn(order),
    ...(footnote === null ? [] : [footnote]),
    { kind: 'heading', text: 'Service' },
    serviceTableEn(order),
    { kind: 'heading', text: 'Total' },
    totalsBlockEn(order),
    { kind: 'note', text: PLANT_BASED_LINE_EN },
  ]
  if (order.gf_guests > 0) blocks.push({ kind: 'note', text: GLUTEN_FREE_NOTICE_EN })
  if (order.lines.some((line) => !allergensAreVerified(line))) blocks.push({ kind: 'note', text: ALLERGEN_EXAMPLE_NOTICE_EN })
  return blocks
}

/* ------------------------------------------------------------------ *
 * Shared pieces
 * ------------------------------------------------------------------ */

/** The register's Reply-To, else the catering recipient (templates.ts's contactAddress). */
const contactAddressEn = (payload: EmailPayload): string =>
  (payload.site.reply_to ?? payload.site.catering_email ?? '').trim()

const keep = (blocks: (Block | null)[]): Block[] => blocks.filter((block): block is Block => block !== null)
const keep2 = (rows: ([string, string] | null)[]): [string, string][] =>
  rows.filter((row): row is [string, string] => row !== null)

const ORDER_MISSING_EN = 'This notification has no order. That is a fault: the code has to be raised with an order.'

function withOrder(payload: EmailPayload, build: (order: OrderPayload) => Email): Email {
  if (payload.order === null) {
    return {
      subject: `[${payload.code}] order missing`,
      preheader: ORDER_MISSING_EN,
      blocks: [{ kind: 'band', text: ORDER_MISSING_EN, tone: 'stop' }],
    }
  }
  return build(payload.order)
}

const greeting = (name: string | null | undefined): Block => ({
  kind: 'paragraph',
  text: name ? `Dear ${name},` : 'Dear Guest,',
})

/** §14's first person plural (DR-14, `build/FIXES.md` item 17) — U21's and
 *  U22's English sign-off, used by every English email. */
const signOff: Block = { kind: 'paragraph', text: 'With our best wishes, The Planteen team' }

const recap = (order: OrderPayload): Block => ({
  kind: 'facts',
  rows: [
    ['Order reference', order.ref],
    ['Event', eventLineEn(order)],
  ],
})

/** §11.4's answer promise in English (U21's draft). */
const ANSWER_PROMISE_EN = 'We aim to answer within 24 hours.'

/** §14 / B14: the ÁSZF and the privacy notice. Until 13 October 2026 this was
 *  a gap note; the legal pages brief (item 5) filled it with the two links,
 *  in the customer's language. */
const termsNote = (payload: EmailPayload): Block[] => legalLinks(payload, 'en')

/** The documents keep their Hungarian names: they are Hungarian documents
 *  (`invoice_language`), and the name is what the customer finds on them. */
const INVOICE_KIND_EN: Record<string, string> = {
  dijbekero: 'Payment request (díjbekérő)',
  szamla: 'Invoice (számla)',
  elolegszamla: 'Advance invoice (előlegszámla)',
  vegszamla: 'Final invoice (végszámla)',
  storno: 'Cancellation invoice (sztornó számla)',
  fee: 'Fee invoice (díjszámla)',
}

const invoiceBlock = (payload: EmailPayload, kind: InvoicePayload['kind']): Block | null => {
  const invoice = [...payload.invoices].reverse().find((candidate) => candidate.kind === kind)
  if (invoice === undefined) return null
  const rows: [string, string][] = [
    [`${INVOICE_KIND_EN[kind] ?? kind}, number`, invoice.szamlazz_number ?? 'being prepared'],
    ['Gross', ftEn(invoice.gross)],
  ]
  if (invoice.issued_at) rows.push(['Issued', dateEn(invoice.issued_at)])
  return { kind: 'facts', rows }
}

const payBy = (order: OrderPayload): string => dateTimeEn(order.payment_deadline)

/** The page's English transfer sentence (Step4Summary's PaymentLine). */
const finalWhenPaid = (order: OrderPayload): string =>
  payBy(order) === ''
    ? 'The order is final once the amount arrives by the payment deadline.'
    : `The order is final once the amount arrives by ${payBy(order)}.`

const transferBlock = (payload: EmailPayload, order: OrderPayload): Block => {
  const rows: [string, string][] = []
  if (payload.site.bank.name) rows.push(['Bank', payload.site.bank.name])
  rows.push([
    'Account number',
    payload.site.bank.account ? payload.site.bank.account : 'MISSING DATA: the bank account number is not set (§9.5)',
  ])
  rows.push(['Amount', ftEn(order.gross)])
  rows.push(['Reference', order.ref])
  if (order.payment_deadline) rows.push(['Payment deadline', payBy(order)])
  return { kind: 'facts', rows }
}

const publicLink = (order: OrderPayload, label = 'View your order'): Block | null =>
  order.public_url ? { kind: 'button', label, href: order.public_url } : null

const refundSentence = (payload: EmailPayload): string => {
  const due = payload.payments.find((payment) => payment.kind === 'refund' && payment.due_at !== null)
  return due
    ? `We start the refund within 5 business days, by ${dateEn(due.due_at ?? '')}.`
    : 'We start the refund within 5 business days.'
}

const CHANGE_REASON_EN: Record<string, string> = {
  dish_unavailable: 'One of the dishes is not available on this day',
  allergen_correction: 'A correction to allergen data',
  delivery: 'A delivery reason',
  force_majeure: 'A reason beyond our control',
  other: 'Another reason',
}
const changeReason = (reason: string | null | undefined): string | null =>
  reason ? (CHANGE_REASON_EN[reason] ?? 'Another reason') : null

/* Run 16 item 3(d) (DECIDED 20261015, Barna): `mod` is the modification fee's
   own base — the changed lines, not the whole order. `of` stays the
   cancellation's, whose wording is unchanged. */
const feeBaseEn = (kind: string | undefined): { label: string; of: string; mod: string } =>
  kind === 'net'
    ? { label: 'Fee base (net)', of: 'of the net price', mod: 'of the net value of the changed items' }
    : { label: 'Fee base (order total)', of: 'of the order total', mod: 'of the value of the changed items' }

const feeBlock = (payload: EmailPayload): Block | null => {
  const fee = payload.fee
  if (fee === null) return null
  const base = feeBaseEn(fee.base_kind)
  const rows: [string, string][] = [
    [base.label, ftEn(fee.base)],
    ['Fee rate', pctEn(fee.pct)],
    ['Fee', fee.base_kind === 'net' ? ftEn(fee.amount) : ftEn(fee.amount_gross)],
    ['Paid so far', ftEn(fee.paid)],
    ['Balance', ftEn(fee.balance)],
  ]
  if (fee.base_kind === 'net') rows.splice(3, 0, ['Fee, gross', ftEn(fee.amount_gross)])
  if (fee.waived) rows.push(['Fee waived', fee.note ?? 'yes'])
  return { kind: 'facts', rows }
}

/** §11.8's ladder in the page's English, every figure the register's. */
const feeLadder = (payload: EmailPayload): string | null => {
  const kind = payload.fee?.base_kind ?? (payload.order?.is_business === true ? 'net' : 'gross')
  const { of, mod: modBase } = feeBaseEn(kind)
  const terms = payload.terms ?? {}
  const figure = (key: FeeTermKey): string | null => {
    const raw = (terms[key] ?? '').trim()
    if (raw === '') return null
    if (key === 'freeze_time') return time(raw) || null
    const value = Number(raw.replace(',', '.'))
    return Number.isFinite(value) ? nEn(value) : null
  }
  const [mod, cancel, within, windowH, noticeBd, freezeTime] = FEE_TERM_KEYS.map(figure)
  if ([mod, cancel, within, windowH, noticeBd, freezeTime].some((value) => value === null)) return null
  /* Run 16 item 3(d): the two fees no longer share a base, so the rung inside
     the fee window names each of them. */
  return (
    `Our change and cancellation fees: free of charge until ${freezeTime}, ${noticeBd} business days before the event; ` +
    `after that a change costs ${mod}% ${modBase} and a cancellation ${cancel}% ${of}; within ${windowH} hours of the event, ` +
    `a change costs ${within}% ${modBase} and a cancellation ${within}% ${of}.`
  )
}
const feeLadderBlock = (payload: EmailPayload, kind: 'paragraph' | 'note'): Block | null => {
  const text = feeLadder(payload)
  return text === null ? null : { kind, text }
}

/* ------------------------------------------------------------------ *
 * The English customer emails
 * ------------------------------------------------------------------ */

export const CUSTOMER_EN: Record<string, (payload: EmailPayload) => Email> = {
  U1: (payload) =>
    withOrder(payload, (order) => ({
      subject: `We have your order · ${order.ref}`,
      preheader: 'A colleague is approving it. We will reply soon.',
      blocks: keep([
        greeting(order.customer_name),
        {
          kind: 'paragraph',
          text: 'Thank you for your order. Your payment has gone through. A colleague approves this order, so it is not confirmed yet.',
        },
        { kind: 'band', text: ANSWER_PROMISE_EN, tone: 'neutral' },
        ...summaryEn(order),
        publicLink(order),
        ...termsNote(payload),
        signOff,
      ]),
    })),

  U2: (payload) =>
    withOrder(payload, (order) => ({
      subject: `Payment request for your order · ${order.ref}`,
      preheader: `Amount due ${ftEn(order.gross)}, reference ${order.ref}.`,
      blocks: keep([
        greeting(order.customer_name),
        {
          kind: 'paragraph',
          text:
            (cardLinkOnDijbekero(payload)
              ? 'Thank you for your order. You receive the payment request (díjbekérő) in a separate email, through Számlázz.hu, ' +
                'with a card payment link and our bank details. '
              : 'Thank you for your order. You receive the payment request (díjbekérő) in a separate email, through Számlázz.hu. ') +
            `${finalWhenPaid(order)} We confirm it once the money has arrived.`,
        },
        { kind: 'heading', text: 'Bank transfer details' },
        transferBlock(payload, order),
        invoiceBlock(payload, 'dijbekero'),
        {
          kind: 'note',
          text:
            'The payment request is not an invoice. We issue the advance invoice (előlegszámla) once the payment has ' +
            'arrived, and you receive it in a separate email too.',
        },
        ...summaryEn(order),
        publicLink(order),
        ...termsNote(payload),
        signOff,
      ]),
    })),

  U3: (payload) =>
    withOrder(payload, (order) => ({
      subject: `Reminder: your order is waiting for payment · ${order.ref}`,
      preheader: finalWhenPaid(order),
      blocks: keep([
        greeting(order.customer_name),
        {
          kind: 'paragraph',
          text:
            (cardLinkOnDijbekero(payload)
              ? 'The payment for your order has not arrived yet. The payment request email from Számlázz.hu carries ' +
                `a card payment link and our bank details. ${finalWhenPaid(order)} `
              : `The bank transfer for your order has not arrived yet. ${finalWhenPaid(order)} `) +
            'If it does not arrive by then, the order and its date are released.',
        },
        recap(order),
        { kind: 'heading', text: 'Bank transfer details' },
        transferBlock(payload, order),
        publicLink(order),
        signOff,
      ]),
    })),

  U4: (payload) =>
    withOrder(payload, (order) => ({
      subject: `Our proposal to change your order · ${order.ref}`,
      preheader: 'We need to change one detail. Please let us know.',
      blocks: keep([
        greeting(order.customer_name),
        {
          kind: 'paragraph',
          text:
            'We have reviewed your order and need to change one detail. Our proposal is the selection below. ' +
            'Please reply to this email to tell us whether you accept it.',
        },
        changeReason(payload.pending_change?.reason)
          ? { kind: 'band', text: `The reason: ${changeReason(payload.pending_change?.reason)}`, tone: 'warn' }
          : null,
        ...summaryEn(order),
        payload.pending_change && payload.pending_change.amount_due !== 0
          ? {
              kind: 'facts',
              rows: [
                [
                  payload.pending_change.amount_due > 0 ? 'Difference to pay' : 'Difference to refund',
                  ftEn(Math.abs(payload.pending_change.amount_due)),
                ],
              ],
            }
          : null,
        {
          kind: 'note',
          text:
            'The change does not take effect until you reply. If you do not accept it, we cancel the order and ' +
            'refund the full amount.',
        },
        signOff,
      ]),
    })),

  U5: (payload) =>
    withOrder(payload, (order) => ({
      subject: `Your order is confirmed · ${order.ref}`,
      preheader: `${dateEn(order.event_date)} · ${nEn(order.guests)} guests · ${ftEn(order.gross)}`,
      blocks: keep([
        greeting(order.customer_name),
        { kind: 'band', text: 'We have confirmed your order. The contract is now in place.', tone: 'good' },
        ...summaryEn(order),
        { kind: 'heading', text: 'Payment and invoice' },
        invoiceBlock(payload, 'elolegszamla') ??
          invoiceBlock(payload, 'szamla') ?? {
            kind: 'note',
            text: 'You receive the advance invoice (előlegszámla) in a separate email, through Számlázz.hu.',
          },
        { kind: 'heading', text: 'Changes and cancellation' },
        feeLadderBlock(payload, 'paragraph'),
        order.freeze_at ? { kind: 'facts', rows: [['Free changes until', dateTimeEn(order.freeze_at)]] } : null,
        {
          kind: 'paragraph',
          // The register's Reply-To, as in the Hungarian (DECIDED 20261012).
          text:
            `To ask for a change or a cancellation, reply to this email, ${contactAddressEn(payload) ? `write to ${contactAddressEn(payload)} ` : ''}or call us, ` +
            'confirmed in writing. A request counts from when it arrives.',
        },
        publicLink(order),
        // Legal pages brief, item 5: the ÁSZF PDF of the order's version.
        termsAttachedNote(order, 'en'),
        ...termsNote(payload),
        signOff,
      ]),
    })),

  U6: (payload) =>
    withOrder(payload, (order) => ({
      subject: `We are sorry: we cannot take on your order · ${order.ref}`,
      preheader: 'We cannot take on the order. We refund anything you have paid.',
      blocks: keep([
        greeting(order.customer_name),
        {
          kind: 'paragraph',
          text: 'We have reviewed your order and, we are sorry, we cannot take it on for this date. We charge no fee of any kind.',
        },
        recap(order),
        order.lane === 'transfer' ? { kind: 'paragraph', text: refundSentence(payload) } : null,
        invoiceBlock(payload, 'storno'),
        { kind: 'paragraph', text: 'If another date might work, write to us — we are glad to find a solution.' },
        signOff,
      ]),
    })),

  U7: (payload) =>
    withOrder(payload, (order) => ({
      subject: `The payment deadline for your order has passed · ${order.ref}`,
      preheader: 'The payment did not arrive in time, so the order has been closed.',
      blocks: keep([
        greeting(order.customer_name),
        {
          kind: 'paragraph',
          text:
            'The payment for your order did not arrive by the deadline, so we have closed the order and released the date. ' +
            'You owe nothing.',
        },
        recap(order),
        order.payment_deadline ? { kind: 'facts', rows: [['Payment deadline was', dateTimeEn(order.payment_deadline)]] } : null,
        { kind: 'paragraph', text: 'If you would still like to order, please place a new order on our page.' },
        signOff,
      ]),
    })),

  U10: (payload) =>
    withOrder(payload, (order) => ({
      subject: `Your change has taken effect · ${order.ref}`,
      preheader: 'Your order stands with the change. The new selection is below.',
      blocks: keep([
        greeting(order.customer_name),
        { kind: 'band', text: 'The change you asked for has taken effect.', tone: 'good' },
        ...summaryEn(order),
        payload.fee !== null ? { kind: 'heading', text: 'Change fee' } : null,
        feeBlock(payload),
        invoiceBlock(payload, 'storno') !== null || invoiceBlock(payload, 'szamla') !== null
          ? { kind: 'heading', text: 'Invoices' }
          : null,
        invoiceBlock(payload, 'storno'),
        invoiceBlock(payload, 'szamla'),
        payload.payments.some((payment) => payment.kind === 'refund') ? { kind: 'paragraph', text: refundSentence(payload) } : null,
        publicLink(order),
        signOff,
      ]),
    })),

  U11: (payload) =>
    withOrder(payload, (order) => ({
      subject: `Your cancellation has taken effect · ${order.ref}`,
      preheader: 'We have cancelled the order. The financial details are below.',
      blocks: keep([
        greeting(order.customer_name),
        { kind: 'paragraph', text: 'As you asked, we have cancelled the order.' },
        recap(order),
        payload.fee !== null ? { kind: 'heading', text: 'Cancellation fee' } : null,
        feeBlock(payload),
        payload.fee !== null ? feeLadderBlock(payload, 'note') : null,
        invoiceBlock(payload, 'storno'),
        { kind: 'paragraph', text: refundSentence(payload) },
        signOff,
      ]),
    })),

  U12: (payload) =>
    withOrder(payload, (order) => ({
      subject:
        order.fulfilment === 'pickup'
          ? `See you on ${onDayEn(order.fulfilment_date ?? order.event_date)} for collection · ${order.ref}`
          : `We are coming on ${onDayEn(order.fulfilment_date ?? order.event_date)} · ${order.ref}`,
      preheader: fulfilmentWhen(order, 'en') || dateEn(order.event_date, true),
      blocks: keep([
        greeting(order.customer_name),
        {
          kind: 'facts',
          rows: [
            ['Order reference', order.ref],
            ['Date', dateEn(order.event_date, true)],
            /* Run 16 item 2: the day-before reminder names the day as well as
               the window — the collection day, which for a weekend event is
               not the event date. */
            order.fulfilment === 'pickup'
              ? (['Collection', fulfilmentWhen(order, 'en') || 'at the agreed time'] as [string, string])
              : (['Delivery window', fulfilmentWhen(order, 'en') || 'in the agreed window'] as [string, string]),
            ['Guests', nEn(order.guests)],
          ],
        },
        {
          kind: 'paragraph',
          text:
            'The food arrives ready to serve: in our own boxes, which it can be served from directly, or arranged on ' +
            'your plates, as agreed.',
        },
        order.gf_guests > 0 ? { kind: 'note', text: GLUTEN_FREE_NOTICE_EN } : null,
        // B74: unwritten slots (§14 cross-contact, B08/B75 consume-by) show nothing.
        signOff,
      ]),
    })),

  U13: (payload) =>
    withOrder(payload, (order) => ({
      subject: `How did it go? · ${order.ref}`,
      preheader: 'Five short questions, and collecting the RevoBox boxes.',
      blocks: keep([
        greeting(order.customer_name),
        {
          kind: 'paragraph',
          text:
            'Thank you for choosing us. If you have a minute, your answers to five short questions help us estimate the ' +
            'quantities more closely next time. Every question can be skipped.',
        },
        {
          kind: 'list',
          items: [
            'How many of you were there in the end?',
            'How was the quantity? (a lot left · a little left · just right · ran out early · not enough)',
            'What ran out first?',
            'What was left over most?',
            'Would you order from us again?',
          ],
        },
        typeof payload.digest?.['survey_url'] === 'string'
          ? { kind: 'button', label: 'Answer the questions', href: payload.digest['survey_url'] as string }
          : { kind: 'note', text: 'MISSING DATA: the survey link did not arrive with the notification.' },
        typeof payload.digest?.['revobox_out'] === 'number' && (payload.digest['revobox_out'] as number) > 0
          ? {
              kind: 'paragraph',
              text: `A colleague will be in touch about the ${nEn(payload.digest['revobox_out'] as number)} RevoBox boxes we delivered.`,
            }
          : null,
        signOff,
      ]),
    })),

  U14: (payload) =>
    withOrder(payload, (order) => ({
      subject: `Updated allergen list · ${order.ref}`,
      preheader: 'We have corrected the allergen data of a dish. The new list is below.',
      blocks: keep([
        greeting(order.customer_name),
        {
          kind: 'paragraph',
          text: 'The allergen data of one of the dishes in your order has been corrected. The food and the price are unchanged; the current list is below.',
        },
        recap(order),
        {
          kind: 'table',
          head: ['Dish', 'Allergens'],
          rows: order.lines.map((line) => [{ ...dishCell(line), indent: false }, { text: allergenTextEn(line) }]),
        },
        lookalikeNoteEn(order.lines),
        order.gf_guests > 0 ? { kind: 'note', text: GLUTEN_FREE_NOTICE_EN } : null,
        { kind: 'paragraph', text: 'If this changes anything on your side, please reply to this email.' },
        signOff,
      ]),
    })),

  U15: (payload) =>
    withOrder(payload, (order) => ({
      subject: `We have your change request · ${order.ref}`,
      preheader: 'Your request has arrived. Your order is unchanged until then.',
      blocks: keep([
        greeting(order.customer_name),
        {
          kind: 'paragraph',
          text:
            'Your change request has reached us. It counts from the time it arrived. A colleague is reviewing it, and ' +
            'we will get back to you before anything changes.',
        },
        recap(order),
        { kind: 'facts', rows: [['Request received', dateTimeEn(payload.created_at)]] },
        { kind: 'paragraph', text: 'Until then your order stands as it is.' },
        feeLadderBlock(payload, 'note'),
        signOff,
      ]),
    })),

  U16: (payload) =>
    withOrder(payload, (order) => ({
      subject: `We are sorry: we have had to cancel your order · ${order.ref}`,
      preheader: 'The Planteen cancelled the order. You owe nothing.',
      blocks: keep([
        greeting(order.customer_name),
        {
          kind: 'paragraph',
          text: 'We are sorry: we have had to cancel your order for a reason on our side. We charge you no fee of any kind.',
        },
        recap(order),
        payload.payments.some((payment) => payment.kind === 'refund') ? { kind: 'paragraph', text: refundSentence(payload) } : null,
        invoiceBlock(payload, 'storno'),
        { kind: 'paragraph', text: 'If another date might work, please get in touch.' },
        signOff,
      ]),
    })),

  U17: (payload) => ({
    subject: 'We have your call-back request',
    preheader: 'We will call you soon.',
    blocks: keep([
      greeting(payload.callback?.name),
      {
        kind: 'paragraph',
        text: 'Thank you for letting us know you would like to talk the quantities through. A colleague will call you soon on the number you gave.',
      },
      payload.callback
        ? {
            kind: 'facts',
            rows: [
              ['Phone number', payload.callback.phone ?? ''],
              ['Received', dateTimeEn(payload.callback.created_at)],
            ],
          }
        : null,
      payload.site.catering_phone
        ? { kind: 'paragraph', text: `If you would like to talk to us sooner, call us: ${payload.site.catering_phone}.` }
        : null,
      signOff,
    ]),
  }),

  U18: (payload) => ({
    subject: 'Your call-back request has expired',
    preheader: 'We could not reach you. You can order on our page at any time.',
    blocks: keep([
      greeting(payload.callback?.name),
      {
        kind: 'paragraph',
        text:
          'Your call-back request has expired. We are sorry if we could not reach you. You can place your order on our ' +
          'page at any time, and we are glad to help by phone too.',
      },
      payload.site.catering_phone ? { kind: 'facts', rows: [['Our phone number', payload.site.catering_phone]] } : null,
      payload.site.site_url ? { kind: 'button', label: 'Place an order', href: payload.site.site_url } : null,
      signOff,
    ]),
  }),

  U19: (payload) =>
    withOrder(payload, (order) => ({
      subject: `Payment link for your change · ${order.ref}`,
      preheader: payload.pending_change ? `Amount due ${ftEn(payload.pending_change.amount_due)}.` : 'The difference for your change is waiting for payment.',
      blocks: keep([
        greeting(order.customer_name),
        {
          kind: 'paragraph',
          text:
            'The change you asked for costs more than the original order. Once the difference is paid, the change takes ' +
            'effect; until then the original order stands.',
        },
        payload.pending_change
          ? {
              kind: 'facts',
              rows: keep2([
                ['Difference to pay', ftEn(payload.pending_change.amount_due)],
                payload.pending_change.expires_at ? ['Link valid until', dateTimeEn(payload.pending_change.expires_at)] : null,
              ]),
            }
          : null,
        payload.fee !== null ? { kind: 'heading', text: 'Change fee' } : null,
        feeBlock(payload),
        payload.pending_change?.payment_ref
          ? { kind: 'button', label: 'Pay the difference', href: payload.pending_change.payment_ref }
          : { kind: 'note', text: 'MISSING DATA: the payment link for this change has not been created yet.' },
        { kind: 'heading', text: 'The changed selection' },
        ...summaryEn(order),
        {
          kind: 'note',
          text:
            'If the difference does not arrive before the link expires, the change does not take effect and the ' +
            'original order stands.',
        },
        signOff,
      ]),
    })),

  U20: (payload) =>
    withOrder(payload, (order) => ({
      subject: `Your change did not take effect · ${order.ref}`,
      preheader: 'The difference did not arrive, so the original order stands.',
      blocks: keep([
        greeting(order.customer_name),
        {
          kind: 'paragraph',
          text:
            'The difference for your change did not arrive before the payment link expired, so the change did not take ' +
            'effect. Your original order stands unchanged, as below.',
        },
        ...summaryEn(order),
        { kind: 'paragraph', text: 'If you would still like the change, reply to this email and we will send a new link.' },
        publicLink(order),
        signOff,
      ]),
    })),

  // `build/FIXES.md` item 79 (F8): the two DRAFT emails' English, as drafted
  // in run 7 — now on the English summary.
  U21: (payload) =>
    withOrder(payload, (order) => ({
      /* Run 19 §14, Fede's review of 16 October: the English half of the
         same three changes — the subject, the body and the button. The
         final price and the answer promise are kept for the reasons the
         Hungarian gives. */
      subject: `Thank you for your order! · ${order.ref}`,
      preheader: 'Nothing to pay yet. A colleague is checking it.',
      blocks: keep([
        greeting(order.customer_name),
        {
          kind: 'paragraph',
          text:
            "Thank you for your order! We've received the details of your event; a colleague will review them and " +
            "get back to you. Nothing is due yet: once they have replied, we'll email you the final price and the " +
            'payment link. We aim to reply within 24 hours.',
        },
        /* §14: the promise is the body's last sentence now, so the band
           that used to carry it would say it twice.
           **It is *reply*, not *answer*.** `ANSWER_PROMISE_EN` reads *We
           aim to answer within 24 hours.*; §14 gives this email's body in
           Fede's own words and they end on *reply*. One word, and the
           difference is deliberate rather than a slip — the B71 copy round
           is where every customer-facing string is read side by side and
           the two can be settled together. */
        ...summaryEn(order),
        publicLink(order, 'View order'),
        ...termsNote(payload),
        signOff,
      ]),
    })),

  U22: (payload) =>
    withOrder(payload, (order) => ({
      subject: `Your offer is confirmed · ${order.ref}`,
      preheader: `${ftEn(order.gross)} — the payment link is in this email.`,
      blocks: keep([
        greeting(order.customer_name),
        { kind: 'band', text: 'We have checked your request and confirmed this offer.', tone: 'good' },
        ...summaryEn(order),
        order.confirm_pay_deadline ? { kind: 'facts', rows: [['Please pay by', dateTimeEn(order.confirm_pay_deadline)]] } : null,
        publicLink(order, 'Pay now'),
        order.public_url
          ? {
              kind: 'paragraph',
              text: `If you would rather not go ahead, you can decline the offer here: ${order.public_url}?decline=1 — nothing is charged.`,
            }
          : null,
        ...termsNote(payload),
        signOff,
      ]),
    })),
}
